// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Description,
  InlineAlert,
  LgPadding,
  SmSpacing,
  XxsSpacing,
} from '@alto/design-system';
import React, { useContext, useEffect, useState } from 'react';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { usePrescription } from '~shared/queries/usePrescription';
import { useSelectorShared } from '~shared/store';
import { AutoRefillInfoButton, AutoRefillRadioOptions } from '../../../../index';
import { useAddToCart } from '../../../cart/hooks/useAddToCart';
import { useUpdateCartItem } from '../../../cart/hooks/useUpdateCartItem';
import { ORDER_TYPE_CONTEXT, type OrderTypeContext } from '../../constants';
import { usePersistOrderType } from '../../hooks/usePersistOrderType';

type OrderTypeActionSheetProps = {
  readonly handleBack?: () => void;
  readonly prescriptionID: number;
  // true: if auto refill should be initially selected,
  // false: if one-time fill should be initially selected,
  // undefined / null: if neither should be selected.
  readonly initialAutoRefillEnabledValue: boolean | undefined | null;
  readonly orderTypeContext: OrderTypeContext;
  readonly beforeAddToCart?: () => void;
};

export const OrderTypeActionSheet = ({
  handleBack,
  prescriptionID,
  orderTypeContext,
  initialAutoRefillEnabledValue,
  beforeAddToCart,
}: OrderTypeActionSheetProps) => {
  const [autoRefillEnabled, setAutoRefillEnabled] = useState(initialAutoRefillEnabledValue);
  const { handleAddToCart, isPending: addToCartLoading } = useAddToCart();
  const { handleUpdateCartItem, isPending: updateCartItemLoading } = useUpdateCartItem();
  const { persistOrderTypeForContext } = usePersistOrderType({
    prescriptionID,
    handleAddToCart,
    handleUpdateCartItem,
  });
  const { goToPreviousActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const { updateAutoRefillLoading } = useSelectorShared(getLoading);
  const { trackPageView, trackEvent } = useAnalytics();
  const { prescription, isLoading: isPrescriptionLoading } = usePrescription({
    prescriptionID,
    enabled: Boolean(prescriptionID),
  });
  const [radioOptionsError, setRadioOptionsError] = useState(false);

  useEffect(() => {
    if (!prescriptionID) return;

    trackPageView({
      event: EVENTS.ORDER_TYPE_SCREEN_VIEWED,
      params: {
        context: orderTypeContext,
      },
      additionalFields: {
        prescriptionId: prescriptionID,
      },
    });
  }, [trackPageView, prescriptionID, orderTypeContext]);

  const handleGoToPreviousActionSheet = () => {
    goToPreviousActionSheet();
    handleBack?.();
  };

  const handleOrderTypeSave = async () => {
    if (autoRefillEnabled === undefined || autoRefillEnabled === null) {
      setRadioOptionsError(true);
      return;
    }

    closeActionSheet();
    beforeAddToCart?.();

    const success = await persistOrderTypeForContext({
      orderTypeContext,
      autoRefillEnabled,
    });

    if (success) {
      trackEvent({
        event: EVENTS.AUTO_REFILL_UI_STATUS_CHANGED,
        params: { origin: getOrigin(), auto_refill_status: autoRefillEnabled ? 'auto refill' : 'one-time fill' },
        additionalFields: { prescriptionId: prescriptionID },
      });
      goToPreviousActionSheet();
    }
  };

  return (
    <ActionSheetV2
      onBack={handleBack}
      title={`Select order type for ${prescription?.medication_name ?? 'this medication'}`}
      buttons={[
        <Button
          testID="order-type-primary-button"
          key="order-type-primary-button"
          label={orderTypeContext === ORDER_TYPE_CONTEXT.ADD_TO_CART ? 'Add to cart' : 'Save'}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={handleOrderTypeSave}
          loading={addToCartLoading || updateCartItemLoading || isPrescriptionLoading}
        />,
        <Button
          key="order-type-cancel-button"
          type="tertiary"
          label="Cancel"
          onPress={handleGoToPreviousActionSheet}
          disabled={updateAutoRefillLoading}
          loading={updateAutoRefillLoading || addToCartLoading}
        />,
      ]}
      analyticsName="auto refill order type"
    >
      <LgPadding
        topPadding={SPACING.STATIC.NONE}
        bottomPadding={SPACING.STATIC.MD}
      >
        <Description testID="order-type-actionsheet-description">This med is eligible for auto refill!</Description>
        <XxsSpacing />
        <AutoRefillInfoButton origin={`${orderTypeContext.toLowerCase()} order type`} />
        {radioOptionsError ? (
          <>
            <SmSpacing />
            <InlineAlert type="error">
              <Description>Please select an order type</Description>
            </InlineAlert>
          </>
        ) : null}
      </LgPadding>
      <AutoRefillRadioOptions
        handleOnChange={(value) => {
          setAutoRefillEnabled(value);
          setRadioOptionsError(false);
        }}
        autoRefillEnabled={autoRefillEnabled}
        prescriptionID={prescriptionID}
      />
    </ActionSheetV2>
  );
};
